import {
  Component,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { AudioFileResource, AudioPostResponse } from '../../shared/resources/audio-file.resource';
import {
  BottomNavClearButtonsList,
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from 'projects/components/src/lib/shared/bottom-nav/bottom-nav.service';
import { TeamsAutoAttendantEditContext } from '../teams-auto-attendant-edit.context';
import { ActivatedRoute, Router } from '@angular/router';
import { TeamsAutoAttendantsResource } from '../resources/teams-auto-attendants.resource';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../shared/services/toast.service';
import { ZiroModalService } from '../../shared/services/ziro-modal.service';
import {
  CallFlowGreetingUpdate,
  TeamsAutoAttendantsCallFlowComponent,
} from './call-flows/teams-auto-attendants-call-flow.component';
import {
  AccountType,
  AutoAttendantHoliday,
  AutoAttendantOperator,
  AutoAttendantOperatorType,
  AutoAttendantsFieldConfig,
  CallFlow,
  CallRoutingOption,
  DialKeys,
  HolidayCallFlow,
  MicrosoftTeamsAutoAttendant,
  MicrosoftTeamsAutoAttendantReferenceView,
  MicrosoftTeamsAutoAttendantView,
  Prompt,
  UserPhoto,
  VoiceAppViewEntity,
} from '../../shared/models/generated/ziroModels';
import { UiCallFlow } from './call-flows/call-flows-side-nav/call-flows-side-nav.component';
import { combineLatest, Observable, of, Subscription, tap } from 'rxjs';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../forms/ziro-forms-models';
import { isEqual } from 'lodash';
import { BottomNavButton } from 'projects/components/src/lib/shared/bottom-nav/bottom-nav.component';
import { ButtonSizes, ButtonStyles, ButtonTypes } from '../../button/button.component';
import { filter, finalize, map, switchMap } from 'rxjs/operators';
import { ZiroModalPromptModalOptions } from '../../modals/prompt-modal/prompt-modal.component';
import { ZiroIcons } from '../../shared/models/ziro-icons.enum';
import { ZiroFormAbstractDirective } from '../../forms/ziro-form-abstract.directive';
import { ZiroSelectConfig, ZiroSelectOption } from '../../forms/fields/select/ziro-select.component';
import { ZiroFormStateService } from '../../forms/ziro-form-state.service';
import { MicrosoftAutoAttendantsSearchResource } from '../../helpdesk/shared/resources/microsoft-auto-attendants-search.resource';
import { MicrosoftCallQueueSearchResource } from '../../helpdesk/shared/resources/microsoft-call-queue-search.resource';
import { MicrosoftTeamsUserSearchResource } from '../../helpdesk/shared/resources/microsoft-teams-user-search.resource';
import { HtmlInputType, ZiroTextConfig } from '../../forms/fields/text/ziro-text.component';
import { transformEnumToHumanReadableLabel } from '../auto-attendants-util';
import { getUserInitialsFromDisplayName } from '../../shared/utils';
import { CallFlowGreetingsOptions } from './call-flows/call-flow-greetings/call-flow-greetings-form.component';
import { TeamsAutoAttendantsViewResource } from '../resources/teams-auto-attendants-view.resource';
import {
  AuthorizedUserDisplay,
  AuthorizedUsersModalComponent,
} from './authorized-users-modal/authorized-users-modal.component';
import { DialScopeModalComponent, DialScopeResult } from './dial-scope-modal/dial-scope-modal.component';

export interface AutoAttendantGeneralSettingsEntity {
  name: string;
  timezone: ZiroSelectOption;
  operatorType: ZiroSelectOption;
  destination: ZiroSelectOption;
  language: ZiroSelectOption;
  resourceAccounts: ZiroSelectOption[];
}

export interface AutoAttendantGeneralSettingsFormData {
  name: string;
  timezone: ZiroSelectOption;
  operatorType: ZiroSelectOption;
  destination: ZiroSelectOption;
  destinationNumber: string;
  language: ZiroSelectOption;
  resourceAccounts: ZiroSelectOption[];
}

@Component({
  selector: 'ziro-teams-auto-attendant-edit-form',
  standalone: false,
  templateUrl: './teams-auto-attendant-edit-form.component.html',
  styleUrls: ['./teams-auto-attendant-edit-form.component.scss'],
})
export class TeamsAutoAttendantEditFormComponent
  extends ZiroFormAbstractDirective<AutoAttendantGeneralSettingsEntity, AutoAttendantGeneralSettingsFormData>
  implements OnInit, OnDestroy, OnChanges
{
  private _audioFileResource = inject(AudioFileResource);
  private _bottomNavService = inject(BottomNavService);
  private _editContext = inject(TeamsAutoAttendantEditContext);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _teamsAutoAttendantsResource = inject(TeamsAutoAttendantsResource);
  private _translateService = inject(TranslateService);
  private _toastService = inject(ToastService);
  private _ziroModalService = inject(ZiroModalService);
  private _msAutoAttendantsSearchResource = inject(MicrosoftAutoAttendantsSearchResource);
  private _msCallQueueSearchResource = inject(MicrosoftCallQueueSearchResource);
  private _msTeamsUserSearchResource = inject(MicrosoftTeamsUserSearchResource);
  private _teamsAutoAttendantsViewResource = inject(TeamsAutoAttendantsViewResource);
  protected _ziroFormStateService = inject(ZiroFormStateService);

  private _returnUrl: string;

  callFlowComponent = viewChild(TeamsAutoAttendantsCallFlowComponent);
  autoAttendantId = input<string>();
  autoAttendantView = input.required<MicrosoftTeamsAutoAttendantView>();
  is360ViewChild = input<boolean>(false);
  tenantId = input<string>();
  autoAttendantsFieldConfig = input<AutoAttendantsFieldConfig>();
  autoAttendantsHolidays = input<AutoAttendantHoliday[]>();
  isLoading = input<boolean>(false);
  isSaving = input<boolean>(false);
  userPhotos = input<UserPhoto[]>();

  get standardCallFlows(): UiCallFlow[] {
    return this._editContext.standardCallFlows();
  }
  get holidayCallFlows(): UiCallFlow[] {
    return this._editContext.localHolidayCallFlows();
  }

  isSubmitting = false;
  formConfig: ZiroFormConfig;
  isLoadingReferences = signal(true);
  references = signal<MicrosoftTeamsAutoAttendantReferenceView[]>([]);
  readonly localAuthorizedUsers = signal<AuthorizedUserDisplay[]>([]);

  private _subscriptions = new Subscription();
  private _callFlowGreetingFiles = new Map<string, File | null>();
  readonly searchedResourceAccountOptions = signal<ZiroSelectOption[]>([]);
  readonly dialScopeInclusionGroups = signal<ZiroSelectOption[]>([]);
  readonly dialScopeExclusionGroups = signal<ZiroSelectOption[]>([]);

  ngOnInit(): void {
    const view = this.autoAttendantView();
    this.dialScopeInclusionGroups.set(
      view.dialScopeInclusionGroups.map((group) => ({ label: group.displayName, value: group.guid })),
    );
    this.dialScopeExclusionGroups.set(
      view.dialScopeExclusionGroups.map((group) => ({ label: group.displayName, value: group.guid })),
    );
    this._returnUrl = this._route.snapshot.queryParamMap.get('returnUrl');
    if (this._returnUrl) {
      this._router.navigate([], {
        relativeTo: this._route,
        queryParams: { returnUrl: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    this._initBottomNav();
    this._initFormConfig();
    this._loadReferences();

    const ziroFormUpdateSub = this.ziroFormsUpdate$.subscribe(
      (data: ZiroFormsUpdate<AutoAttendantGeneralSettingsEntity>) => {
        if (this.isFormSubmitted) {
          const isGeneralSettingsFormValid = this.isFormValid();
          const areCallFlowsValid = this.callFlowComponent().areCallFlowComponentsValid();
          this._setBottomNavValidationError(!isGeneralSettingsFormValid || !areCallFlowsValid);
        }
        const operatorTypeChanged = !isEqual(data.new.operatorType, data.old.operatorType);
        const destinationChanged = !isEqual(data.new.destination, data.old.destination);

        const nameChanged = data.new.name !== data.old.name;
        const languageChanged = data.new.language?.value !== data.old.language?.value;
        const timezoneChanged = data.new.timezone?.value !== data.old.timezone?.value;

        const generalSettingsChanged =
          nameChanged || languageChanged || timezoneChanged || operatorTypeChanged || destinationChanged;

        if (generalSettingsChanged) {
          const update: Partial<Pick<MicrosoftTeamsAutoAttendant, 'name' | 'language' | 'timeZone' | 'operator'>> = {};

          if (nameChanged) {
            update.name = data.new.name;
          }
          if (languageChanged) {
            update.language = data.new.language?.value ?? null;
          }
          if (timezoneChanged) {
            update.timeZone = data.new.timezone?.value ?? null;
          }

          if (operatorTypeChanged || destinationChanged) {
            const operatorTypeValue = data.new.operatorType?.value;
            update.operator =
              operatorTypeValue && operatorTypeValue !== AutoAttendantOperatorType.NOT_SUPPORTED
                ? ({
                    configurationEndpointType: '',
                    destination: data.new.destination?.value ?? '',
                    type: operatorTypeValue,
                  } as AutoAttendantOperator)
                : null;
          }

          this._editContext.updateGeneralSettings(update);
        }

        if (!isEqual(data.new.resourceAccounts, data.old.resourceAccounts)) {
          this._editContext.updateResourceAccountGuids(data.new.resourceAccounts ?? []);
        }

        if (operatorTypeChanged) {
          const resetEntity: AutoAttendantGeneralSettingsEntity = {
            ...data.new,
            destination: null,
          };
          this.entitySource.next(resetEntity);
        }
      },
    );

    this._subscriptions.add(ziroFormUpdateSub);

    const formSubmitSub = this._validateAndSubmitSource.subscribe(() => {
      const isGeneralSettingsFormValid = this.isFormValid();
      const areCallFlowsValid = this.callFlowComponent().areCallFlowComponentsValid();
      this._setBottomNavValidationError(!isGeneralSettingsFormValid || !areCallFlowsValid);
    });
    this._subscriptions.add(formSubmitSub);
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes['autoAttendantView']?.currentValue) {
      this.localAuthorizedUsers.set(
        this.autoAttendantView().users.map((u) => ({
          guid: u.guid,
          displayName: u.displayName,
          upn: u.upn,
          photoBase64: this.getUserPhoto(u) ?? '',
        })),
      );
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._bottomNavService.dispatch(new BottomNavClearButtonsList());
  }

  getUserPhoto(user: VoiceAppViewEntity): string | null {
    return this.userPhotos().find((photo) => photo.userPrincipalName === user.upn)?.photoBase64 ?? null;
  }

  getUserInitials(displayName: string): string {
    return getUserInitialsFromDisplayName(displayName);
  }

  getDialScopeDescription(): string {
    const dialScopeInclusions = this.dialScopeInclusionGroups();
    const dialScopeExclusions = this.dialScopeExclusionGroups();
    if (dialScopeInclusions.length === 0 && dialScopeExclusions.length === 0) {
      return this._translateService.instant(
        'tkey;admin.teams_voice_management.teams_auto_attendants.dial_scope.everyone',
      );
    }
    const inclusionsDescription =
      dialScopeInclusions.length > 0
        ? this._translateService.instant(
            'tkey;admin.teams_voice_management.teams_auto_attendants.dial_scope.inclusions',
            { count: dialScopeInclusions.length },
          )
        : this._translateService.instant(
            'tkey;admin.teams_voice_management.teams_auto_attendants.dial_scope.inclusions_all',
          );
    const exclusionsDescription =
      dialScopeExclusions.length > 0
        ? this._translateService.instant(
            'tkey;admin.teams_voice_management.teams_auto_attendants.dial_scope.exclusions',
            { count: dialScopeExclusions.length },
          )
        : this._translateService.instant(
            'tkey;admin.teams_voice_management.teams_auto_attendants.dial_scope.exclude_no_one',
          );
    return `${inclusionsDescription}, ${exclusionsDescription}`;
  }

  openAuthorizedUsersModal(): void {
    const options = {
      size: 'lg',
      bodyClass: AuthorizedUsersModalComponent,
      modalViewProperties: {
        title: 'tkey;admin.teams_voice_management.teams_auto_attendant.authorized_users.modal.title',
        submitButtonLabel: 'tkey;global.button.continue.text',
        tenantId: this.tenantId(),
        initialUsers: this.localAuthorizedUsers(),
        submitFn: (users: AuthorizedUserDisplay[]) => of(users),
      },
    };

    this._ziroModalService
      .openDetailedModal(() => options.modalViewProperties, options)
      .pipe(filter((result): result is AuthorizedUserDisplay[] => !!result))
      .subscribe((updatedUsers) => {
        this._editContext.updateAuthorizedUserGuids(updatedUsers.map((u) => u.guid));
        this.localAuthorizedUsers.set(updatedUsers);
      });
  }

  openDialScopeModal(): void {
    const aa = this._editContext.autoAttendant();
    const hasInclusionGroups = aa?.dialScopeInclusionGroups?.length;
    const hasExclusionGroups = aa?.dialScopeExclusionGroups?.length;

    this._ziroModalService
      .openDetailedModal(
        () => ({
          title: 'tkey;admin.teams_voice_management.teams_auto_attendant.dial_scope.modal.title',
          displayCloseButton: true,
          submitButtonLabel: 'tkey;global.button.continue.text',
          tenantId: this.tenantId(),
          inclusionMode: hasInclusionGroups ? 'CUSTOM' : 'ALL',
          exclusionMode: hasExclusionGroups ? 'CUSTOM' : 'NONE',
          inclusionGroups: this.dialScopeInclusionGroups(),
          exclusionGroups: this.dialScopeExclusionGroups(),
        }),
        {
          bodyClass: DialScopeModalComponent,
          size: 'lg',
        },
      )
      .subscribe((result: DialScopeResult) => {
        if (result) {
          this.dialScopeInclusionGroups.set(result.inclusionGroups);
          this.dialScopeExclusionGroups.set(result.exclusionGroups);
          this._editContext.updateDialScope(
            result.inclusionGroups.map((group) => group.value),
            result.exclusionGroups.map((group) => group.value),
          );
        }
      });
  }

  onCallFlowGreetingUpdate(update: CallFlowGreetingUpdate): void {
    const isCustomGreeting = update.greeting.selectedGreetingOption === CallFlowGreetingsOptions.CUSTOM_GREETING;
    const key = update.greeting.isDestinationsGreeting ? `${update.flowId}:destination` : `${update.flowId}:callflow`;
    this._callFlowGreetingFiles.set(key, isCustomGreeting ? update.greeting.greetingFile : null);
  }

  toFormData = (entity: AutoAttendantGeneralSettingsEntity): AutoAttendantGeneralSettingsFormData => {
    return {
      name: entity.name,
      timezone: entity.timezone,
      operatorType: entity.operatorType ? this._toOperatorTypeOption(entity.operatorType.value) : null,
      destination:
        entity.operatorType?.value === AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER ? null : entity.destination,
      destinationNumber:
        entity.operatorType?.value === AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER
          ? entity.destination?.label
          : null,
      language: entity.language,
      resourceAccounts: entity.resourceAccounts ?? [],
    };
  };

  toEntity = (formData: AutoAttendantGeneralSettingsFormData): AutoAttendantGeneralSettingsEntity => {
    return {
      name: formData.name,
      timezone: formData.timezone,
      operatorType: formData.operatorType,
      destination:
        formData.operatorType?.value === AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER
          ? { label: formData.destinationNumber, value: formData.destinationNumber }
          : formData.destination,
      language: formData.language,
      resourceAccounts: formData.resourceAccounts ?? [],
    };
  };

  private _initBottomNav(): void {
    const bottomNavButtons: BottomNavButton[] = [];
    bottomNavButtons.push({
      id: 'cancel-button',
      dataAutomation: 'auto-attendant-cancel-button',
      label: 'tkey;global.button.cancel.text',
      buttonClass: ButtonStyles.DEFAULT,
      cb: () => {
        this._navigateAfterAction();
      },
    });
    if (this.autoAttendantId()) {
      bottomNavButtons.push({
        id: 'delete-button',
        dataAutomation: 'auto-attendant-delete-button',
        label: 'tkey;global.button.delete.text',
        icon: ZiroIcons.DELETE_SMALL,
        buttonClass: ButtonStyles.DANGER,
        cb: () => this._onDeleteClick(),
      });
    }
    bottomNavButtons.push({
      id: 'save-button',
      dataAutomation: 'auto-attendant-save-button',
      label: 'tkey;global.button.save.text',
      icon: ZiroIcons.SAVE_SUCCESS,
      buttonClass: ButtonStyles.PRIMARY,
      type: ButtonTypes.SUBMIT,
      submitSubject: this._validateAndSubmitSource,
    });
    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(bottomNavButtons));
  }

  private _initFormConfig(): void {
    const timeZoneOptions = this.autoAttendantsFieldConfig().timeZones.map((timeZone) => ({
      label: timeZone.displayName,
      value: timeZone.id,
    }));
    const languageOptions = this.autoAttendantsFieldConfig().languages.map((language) => ({
      label: language.displayName,
      value: language.code,
    }));

    this.formConfig = {
      options: {
        columnClasses: {
          label: 'col-sm-1',
          input: 'col-sm-1',
        },
      },
      fields: {
        name: {
          label: () => 'tkey;admin.teams_voice_management.teams_auto_attendant.general_settings.name.label',
          dataAutomation: 'general-settings-name',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.TEXT,
          }),
          required: true,
          disabled: () => this.isSubmitting,
        },
        timezone: {
          label: () => 'tkey;admin.teams_voice_management.teams_auto_attendant.general_settings.timezone.label',
          dataAutomation: 'general-settings-timezone',
          componentConfig: new ZiroSelectConfig({ options: timeZoneOptions }),
          required: true,
          disabled: () => this.isSubmitting,
        },
        operatorType: {
          label: () => 'tkey;admin.teams_voice_management.teams_auto_attendant.general_settings.operator_type.label',
          dataAutomation: 'general-settings-operatorType',
          componentConfig: new ZiroSelectConfig({
            options: Object.values(AutoAttendantOperatorType).map((value) => this._toOperatorTypeOption(value)),
            hideClear: true,
          }),
          required: true,
          disabled: () => this.isSubmitting,
        },
        destination: {
          label: () =>
            'tkey;admin.teams_voice_management.teams_auto_attendant.general_settings.operator_destination.label',
          dataAutomation: 'general-settings-destination',
          componentConfig: new ZiroSelectConfig({
            isMultiSelect: false,
            keepSearchInput: true,
            minSearchLength: 3,
            asyncOptionsFn: this._fetchDestinationSearchResults.bind(this),
          }),
          disabled: () =>
            this.isSubmitting || this.formData.operatorType?.value === AutoAttendantOperatorType.NOT_SUPPORTED,
          valExcluded: () =>
            this.formData.operatorType?.value === AutoAttendantOperatorType.NOT_SUPPORTED ||
            this.formData.operatorType?.value === AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER,
          required: () => this.formData.operatorType?.value !== AutoAttendantOperatorType.NOT_SUPPORTED,
          hidden: () =>
            this.formData.operatorType?.value === AutoAttendantOperatorType.NOT_SUPPORTED ||
            this.formData.operatorType?.value === AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER,
        },
        destinationNumber: {
          label: () =>
            'tkey;admin.teams_voice_management.teams_auto_attendant.general_settings.operator_destination.label',
          dataAutomation: 'general-settings-destination-number',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: () => this.formData.operatorType?.value === AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER,
          hidden: () => this.formData.operatorType?.value !== AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER,
          valExcluded: () => this.formData.operatorType?.value !== AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER,
          disabled: () => this.isSubmitting,
          validation: [
            {
              validator: (val: number) =>
                Number.isInteger(Number(val)) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
              message: 'tkey;validators.global.error.invalid_number.pattern',
            },
          ],
        },
        language: {
          label: () => 'tkey;admin.teams_voice_management.teams_auto_attendant.general_settings.language.label',
          dataAutomation: 'general-settings-language',
          componentConfig: new ZiroSelectConfig({
            options: languageOptions,
          }),
          required: true,
          disabled: () => this.isSubmitting,
        },
        resourceAccounts: {
          label: 'tkey;admin.teams_voice_management.teams_auto_attendant.general_settings.resource_accounts.label',
          dataAutomation: 'general-settings-resource-accounts',
          componentConfig: new ZiroSelectConfig({
            isMultiSelect: true,
            minSearchLength: 3,
            asyncOptionsFn: this._searchResourceAccountsWithCache.bind(this),
            hideClear: true,
          }),
          disabled: () => this.isSubmitting,
        },
      },
    };
  }

  private _onDeleteClick(): void {
    const options: ZiroModalPromptModalOptions = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: 'text-danger',
        promptBody: this._translateService.instant(
          'tkey;admin.teams_voice_management.teams_auto_attendant.delete.confirmation.modal.prompt',
          {
            name: this.autoAttendantView()?.name,
          },
        ),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._deleteAutoAttendant(),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _deleteAutoAttendant(): Observable<void> {
    this._setPending('save-button', true);
    this._setDisabled('delete-button', true);
    this._setDisabled('cancel-button', true);
    return this._teamsAutoAttendantsResource.deleteAutoAttendant(this.tenantId(), this.autoAttendantId()).pipe(
      tap(() => {
        this._toastService.pushDeleteToast(
          'tkey;admin.teams_voice_management.teams_auto_attendants.toast.service_type',
          this.autoAttendantView()?.name,
        );
        this._navigateAfterAction();
      }),
    );
  }

  private _patchAudioIdIntoPayload(
    flowId: string,
    audioId: string,
    downloadUri: string,
    name: string,
    isDestinationsGreeting = false,
  ): void {
    const allFlows: UiCallFlow[] = [...this.standardCallFlows, ...this.holidayCallFlows];
    const flow = allFlows.find((f) => f.id === flowId);
    if (!flow) {
      return;
    }

    const updatedPrompt: Prompt = {
      audio: { id: audioId, downloadUri, name },
      ttsMessage: null,
    };

    this._editContext.updateGreeting(
      flowId,
      updatedPrompt,
      CallFlowGreetingsOptions.CUSTOM_GREETING,
      isDestinationsGreeting,
    );
  }

  private _setPending(buttonId: string, setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: buttonId,
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  private _setDisabled(buttonId: string, setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: buttonId,
        state: {
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  private _searchResourceAccounts(query: string): Observable<ZiroSelectOption[]> {
    return this._msTeamsUserSearchResource.search(this.tenantId(), query, null, 250, AccountType.RESOURCE_ACCOUNT).pipe(
      map((users) =>
        users.map((user) => ({
          label: user.ref?.displayName ?? user.userPrincipalName,
          value: user.guid,
        })),
      ),
    );
  }

  private _searchResourceAccountsWithCache = (searchTerm: string): Observable<ZiroSelectOption[]> => {
    return this._msTeamsUserSearchResource
      .search(this.tenantId(), searchTerm, null, 250, AccountType.RESOURCE_ACCOUNT)
      .pipe(
        map((results) =>
          results.map((account) => ({
            label: account.ref?.displayName ?? account.userPrincipalName,
            value: account.guid,
          })),
        ),
        tap((results) => {
          const merged = [...this.searchedResourceAccountOptions(), ...results];
          const unique = new Map<string, ZiroSelectOption>();

          for (const option of merged) {
            unique.set(option.value, option);
          }

          this.searchedResourceAccountOptions.set(Array.from(unique.values()));
        }),
      );
  };

  private _toOperatorTypeOption(operatorType: string): ZiroSelectOption {
    if (!operatorType) {
      return null;
    }

    const labelOverrides: Partial<Record<AutoAttendantOperatorType, string>> = {
      [AutoAttendantOperatorType.APPLICATION_ENDPOINT]: 'Resource Account',
      [AutoAttendantOperatorType.CONFIGURATION_ENDPOINT]: 'Voice Application',
      [AutoAttendantOperatorType.NOT_SUPPORTED]: 'None',
    };
    return {
      label:
        labelOverrides[operatorType as AutoAttendantOperatorType] ?? transformEnumToHumanReadableLabel(operatorType),
      value: operatorType,
    };
  }

  private _fetchDestinationSearchResults(query: string): Observable<ZiroSelectOption[] | string[]> {
    const operatorType = this.formData.operatorType?.value;
    if (!query || query.length < 3) {
      return of([]);
    }

    if (operatorType === AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER) {
      return of([
        {
          label: query,
          value: query,
        },
      ]);
    }

    if (operatorType === AutoAttendantOperatorType.USER) {
      return this._msTeamsUserSearchResource.search(this.tenantId(), query, true, 250, AccountType.USER).pipe(
        map((users) =>
          users.map((user) => ({
            label: user.ref?.displayName ?? user.userPrincipalName,
            value: user.guid,
          })),
        ),
      );
    }

    if (operatorType === AutoAttendantOperatorType.APPLICATION_ENDPOINT) {
      return this._searchResourceAccounts(query);
    }

    if (operatorType === AutoAttendantOperatorType.CONFIGURATION_ENDPOINT) {
      return combineLatest([
        this._msCallQueueSearchResource.search(this.tenantId(), query),
        this._msAutoAttendantsSearchResource.search(this.tenantId(), query),
      ]).pipe(
        map(([callQueues, autoAttendants]) => [
          ...callQueues.map((callQueue) => ({
            label: callQueue.name,
            value: callQueue.callQueueId,
            group: 'Call Queue',
          })),
          ...autoAttendants.map((autoAttendant) => ({
            label: autoAttendant.name,
            value: autoAttendant.id,
            group: 'Auto Attendant',
          })),
        ]),
      );
    }

    return of([]);
  }

  protected submit(): Observable<null> {
    this.isFormSubmitted = true;
    this.validateAllFields();

    if (!this._isFormReadyToSubmit()) {
      this._bottomNavService.setBottomNavValidationError(true);
      return of(null);
    }

    const payload = this._editContext.autoAttendant();
    if (!payload) {
      return of(null);
    }

    this._setSavingState(true);

    return this._uploadGreetingFiles().pipe(
      tap((uploadResults) => this._applyUploadedGreetingFiles(uploadResults)),
      switchMap(() => this._uploadMenuButtonAudioFiles()),
      tap((uploadResults) => this._applyUploadedMenuButtonAudioFiles(uploadResults)),
      switchMap(() => this._saveAutoAttendant()),
      tap(() => this._handleSaveSuccess()),
      finalize(() => this._setSavingState(false)),
      map((): null => null),
    );
  }

  private _isFormReadyToSubmit(): boolean {
    const isGeneralSettingsFormValid = this.isFormValid();
    const areCallFlowsValid = this.callFlowComponent().areCallFlowComponentsValid();
    this._setBottomNavValidationError(!isGeneralSettingsFormValid || !areCallFlowsValid);
    return isGeneralSettingsFormValid && areCallFlowsValid;
  }

  private _setSavingState(isSaving: boolean): void {
    this.isSubmitting = isSaving;
    this._setPending('save-button', isSaving);
    this._setDisabled('delete-button', isSaving);
    this._setDisabled('cancel-button', isSaving);
  }

  private _uploadGreetingFiles(): Observable<{ key: string; fileName: string; response: AudioPostResponse }[]> {
    const uploads = Array.from(this._callFlowGreetingFiles.entries())
      .filter(([, file]) => !!file)
      .map(([key, file]) =>
        this._audioFileResource.createAudioFile(this.tenantId(), 'auto-attendant', file).pipe(
          map((response: AudioPostResponse) => ({
            key,
            fileName: file.name,
            response,
          })),
        ),
      );

    return uploads.length ? combineLatest(uploads) : of([]);
  }

  private _applyUploadedGreetingFiles(
    uploadResults: { key: string; fileName: string; response: AudioPostResponse }[],
  ): void {
    uploadResults.forEach(({ key, response, fileName }) => {
      const [flowId, greetingType] = key.split(':');
      const isDestinationsGreeting = greetingType === 'destination';
      this._patchAudioIdIntoPayload(flowId, response.id, response.url, fileName, isDestinationsGreeting);
    });
  }

  private _saveAutoAttendant(): Observable<void> {
    const autoAttendant = this._editContext.autoAttendant();
    const isRegularHoursConfigured = !!autoAttendant.regularHours;

    const payload = this._normalizePayloadGreetingsForSave({
      ...autoAttendant,
      businessHoursCallFlow: this._normalizeCallFlowForSave(autoAttendant.businessHoursCallFlow),
      afterHoursCallFlow: isRegularHoursConfigured
        ? this._normalizeCallFlowForSave(autoAttendant.afterHoursCallFlow)
        : null,

      holidayCallFlows: this._toHolidayCallFlows().map((holidayCallFlow) => ({
        ...holidayCallFlow,
        callFlow: this._normalizeCallFlowForSave(holidayCallFlow.callFlow),
      })),
    });

    return this.autoAttendantId()
      ? this._teamsAutoAttendantsResource.updateAutoAttendant(this.tenantId(), this.autoAttendantId(), payload)
      : this._teamsAutoAttendantsResource.createAutoAttendant(this.tenantId(), payload);
  }

  private _normalizeCallFlowForSave(callFlow: CallFlow | null | undefined): CallFlow | null {
    if (!callFlow) {
      return null;
    }

    const isPlayMenuOptions = callFlow.callRoutingOption === CallRoutingOption.PLAY_MENU_OPTIONS;
    const isRedirectCall = callFlow.callRoutingOption === CallRoutingOption.REDIRECT_CALL;

    let playMenuOptionSettings =
      isPlayMenuOptions && callFlow.playMenuOptionSettings
        ? this._editContext.removeEntityFromObject(callFlow.playMenuOptionSettings)
        : isPlayMenuOptions
          ? callFlow.playMenuOptionSettings
          : null;

    if (isPlayMenuOptions && playMenuOptionSettings) {
      const hasButtons = Object.values(playMenuOptionSettings.dialKeys ?? {}).some(Boolean);
      if (!hasButtons) {
        playMenuOptionSettings = { ...playMenuOptionSettings, dialKeys: this._emptyDialKeys() };
      }
    }

    const redirectCallSettings =
      isRedirectCall && callFlow.redirectCallSettings
        ? this._editContext.removeEntityFromObject(callFlow.redirectCallSettings)
        : isRedirectCall
          ? callFlow.redirectCallSettings
          : null;

    return {
      ...callFlow,
      playMenuOptionSettings,
      redirectCallSettings,
    };
  }

  private _emptyDialKeys(): DialKeys {
    return {
      button0: null,
      button1: null,
      button2: null,
      button3: null,
      button4: null,
      button5: null,
      button6: null,
      button7: null,
      button8: null,
      button9: null,
      buttonPound: null,
      buttonStar: null,
    };
  }

  private _toHolidayCallFlows(): HolidayCallFlow[] {
    return this._editContext.localHolidayCallFlows().map((flow) => ({
      schedule: flow.holiday ?? null,
      callFlow: {
        name: flow.name,
        callRoutingOption: flow.callRoutingOption,
        greetingOption: flow.greetingOption ?? null,
        playMenuOptionSettings: flow.playMenuOptionSettings ?? null,
        redirectCallSettings: flow.redirectCallSettings ?? null,
      },
    }));
  }

  private _normalizePromptForSave(greeting: Prompt | null, selectedOption?: CallFlowGreetingsOptions): Prompt | null {
    switch (selectedOption) {
      case CallFlowGreetingsOptions.NONE:
        return null;
      case CallFlowGreetingsOptions.CUSTOM_GREETING:
        return greeting ? { ...greeting, ttsMessage: null } : null;
      case CallFlowGreetingsOptions.TTS_MESSAGE:
        return greeting ? { ...greeting, audio: null } : null;
      default:
        return greeting;
    }
  }

  private _normalizePayloadGreetingsForSave(payload: MicrosoftTeamsAutoAttendant): MicrosoftTeamsAutoAttendant {
    const standardFlows = this.standardCallFlows;
    const holidayFlows = this.holidayCallFlows;

    const businessHoursFlow = standardFlows.find((flow) => !flow.isAfterHoursCallFlow);
    const afterHoursFlow = standardFlows.find((flow) => flow.isAfterHoursCallFlow);

    return {
      ...payload,
      businessHoursCallFlow: payload.businessHoursCallFlow
        ? {
            ...payload.businessHoursCallFlow,
            greetingOption: this._normalizePromptForSave(
              payload.businessHoursCallFlow.greetingOption,
              businessHoursFlow?.selectedGreetingOption,
            ),
          }
        : payload.businessHoursCallFlow,
      afterHoursCallFlow: payload.afterHoursCallFlow
        ? {
            ...payload.afterHoursCallFlow,
            greetingOption: this._normalizePromptForSave(
              payload.afterHoursCallFlow.greetingOption,
              afterHoursFlow?.selectedGreetingOption,
            ),
          }
        : payload.afterHoursCallFlow,
      holidayCallFlows: payload.holidayCallFlows.map((holidayCallFlow) => {
        const matchingUiFlow = holidayFlows.find((flow) => flow.holiday?.id === holidayCallFlow.schedule?.id);
        return {
          ...holidayCallFlow,
          callFlow: {
            ...holidayCallFlow.callFlow,
            greetingOption: this._normalizePromptForSave(
              holidayCallFlow.callFlow.greetingOption,
              matchingUiFlow?.selectedGreetingOption,
            ),
          },
        };
      }),
    };
  }

  private _setBottomNavValidationError(hasError: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: hasError,
      }),
    );
  }

  private _handleSaveSuccess(): void {
    const payload = this._editContext.autoAttendant();

    this._toastService.pushSaveToast(
      'tkey;admin.teams_voice_management.teams_auto_attendants.toast.service_type',
      payload?.name,
      ZiroIcons.AUTO_ATTENDANT,
    );

    this._navigateAfterAction();
  }

  private _uploadMenuButtonAudioFiles(): Observable<
    { flowId: string; prop: keyof DialKeys; fileName: string; response: AudioPostResponse }[]
  > {
    const allFlows = [...this.standardCallFlows, ...this.holidayCallFlows];
    const uploads: Observable<{
      flowId: string;
      prop: keyof DialKeys;
      fileName: string;
      response: AudioPostResponse;
    }>[] = [];

    for (const flow of allFlows) {
      const buttons = this._editContext.getButtonsForFlow(flow.id);
      for (const btn of buttons) {
        if (btn.audioFile) {
          const file = btn.audioFile;
          uploads.push(
            this._audioFileResource.createAudioFile(this.tenantId(), 'auto-attendant', file).pipe(
              map((response: AudioPostResponse) => ({
                flowId: flow.id,
                prop: btn.prop,
                fileName: file.name,
                response,
              })),
            ),
          );
        }
      }
    }

    return uploads.length ? combineLatest(uploads) : of([]);
  }

  private _applyUploadedMenuButtonAudioFiles(
    uploadResults: { flowId: string; prop: keyof DialKeys; fileName: string; response: AudioPostResponse }[],
  ): void {
    for (const { flowId, prop, fileName, response } of uploadResults) {
      this._editContext.patchDialKeyAudioInfo(
        flowId,
        prop,
        {
          fileName,
          downloadUri: response.url,
        },
        response.id,
      );
    }
  }

  private _loadReferences(): void {
    if (!this.autoAttendantId()) {
      this.isLoadingReferences.set(false);
      this.references.set([]);
      return;
    }

    this.isLoadingReferences.set(true);

    const referencesSub = this._teamsAutoAttendantsViewResource
      .getReferences(this.tenantId(), this.autoAttendantId())
      .pipe(finalize(() => this.isLoadingReferences.set(false)))
      .subscribe((refs) => this.references.set(refs));

    this._subscriptions.add(referencesSub);
  }

  private _navigateAfterAction(): void {
    if (this._returnUrl) {
      this._router.navigateByUrl(this._returnUrl);
    } else {
      const navigationFragment = !this.is360ViewChild() ? '../' : '../..';
      this._router.navigate([navigationFragment], { relativeTo: this._route });
    }
  }

  protected readonly ZiroIcons = ZiroIcons;
  protected readonly ButtonSizes = ButtonSizes;
  protected readonly AutoAttendantOperatorType = AutoAttendantOperatorType;
  protected readonly ButtonStyles = ButtonStyles;
}
